// Educational Resource Pages (Tier 6)
const pages = [
  {
    slug: '/what-is-recovery-housing/',
    category: 'Educational Resource', tier: 'Tier 6',
    name: 'What Is Recovery Housing?',
    primaryKeyword: 'What Is Recovery Housing',
    keywords: ['recovery housing meaning', 'what is a recovery house', 'recovery housing explained'],
    title: 'What Is Recovery Housing? A Plain-Language Guide | PGSRH',
    description: 'What is recovery housing? It’s a safe, structured, substance-free home for people in recovery. Learn how it works and how PGSRH homes support the Lehigh Valley, PA.',
    blurb: 'A plain-language explanation of recovery housing.',
    heroSubtitle: 'A clear, plain-language explanation of recovery housing — what it is, how it works, and who it helps.',
    intro: [
      'Recovery housing is a safe, substance-free place to live for people in recovery from drug or alcohol use. Residents live together, follow shared expectations, and support one another — all in an environment designed to protect and strengthen recovery.',
      'It’s important to understand what recovery housing is not: it’s not detox, rehab, therapy, or medical treatment. It’s housing with structure and community.',
    ],
    whoFor: [
      'People new to the idea of recovery housing',
      'Families researching options for a loved one',
      'Individuals deciding whether it fits their situation',
      'Anyone comparing recovery housing to other options',
    ],
    sections: [
      { h: 'The core idea', p: [
        'At its heart, recovery housing gives people a stable home and a community of peers during a stage when both are fragile. Structure, routine, and accountability replace the chaos that often surrounds active addiction.',
        'Different names — recovery residence, sober living, recovery home — all point to this same idea.',
      ] },
      { h: 'How PGSRH fits in', p: [
        'PGSRH operates recovery housing for men and women in the Lehigh Valley, Pennsylvania, as DDAP Licensed Recovery Residences. Our homes provide the substance-free environment, expectations, and peer support that define recovery housing.',
        'From here you can explore how recovery housing compares to sober living, what daily life looks like, and the typical rules of a recovery home.',
      ] },
    ],
    faqs: [
      { q: 'What is recovery housing in simple terms?', a: 'A safe, substance-free home where people in recovery live together with shared expectations and peer support. It is housing, not clinical treatment.' },
      { q: 'Is recovery housing the same as rehab?', a: 'No. Rehab is clinical treatment. Recovery housing is a supportive place to live, often after treatment. PGSRH provides housing, not treatment.' },
      { q: 'Who lives in recovery housing?', a: 'Adults in recovery who are committed to a substance-free lifestyle and willing to follow house expectations.' },
      { q: 'Does recovery housing cost money?', a: 'Yes, there is a cost to living in recovery housing. Funding or scholarship options may be available depending on eligibility and availability — ask PGSRH.' },
    ],
    funding: false,
  },
  {
    slug: '/recovery-housing-vs-sober-living/',
    category: 'Educational Resource', tier: 'Tier 6',
    name: 'Recovery Housing vs Sober Living',
    primaryKeyword: 'Recovery Housing vs Sober Living',
    keywords: ['sober living vs recovery housing', 'recovery residence vs sober living'],
    title: 'Recovery Housing vs Sober Living: What’s the Difference? | PGSRH',
    description: 'Recovery housing vs sober living — what’s the difference? In most cases they mean the same thing. Learn the terminology and how PGSRH homes work in the Lehigh Valley, PA.',
    blurb: 'Understand the terminology and choose the right path.',
    heroSubtitle: 'Recovery housing, sober living, recovery residence — what do these terms actually mean, and how do you choose?',
    intro: [
      'People often ask whether "recovery housing" and "sober living" are different things. In everyday use — and at PGSRH — they describe the same core idea: a structured, substance-free home with peer support.',
      'Understanding the terminology helps you search with confidence and compare options apples-to-apples.',
    ],
    whoFor: [
      'People confused by the different terms',
      'Families comparing options for a loved one',
      'Anyone deciding which type of home fits',
      'Referral partners explaining options to clients',
    ],
    sections: [
      { h: 'Same idea, different words', p: [
        '"Recovery residence" is the formal term used by many organizations. "Sober living" is the phrase most people use in conversation. "Recovery housing" and "recovery home" are common too. All describe a substance-free home with structure and peer support.',
        'What matters more than the label is the quality of the home: its expectations, its community, and its stability.',
      ] },
      { h: 'How to choose', p: [
        'Focus on the things that actually differ home to home: gender-specific vs. mixed, location, structure and expectations, and current availability. Then find the home that fits your needs and keeps you near your support system.',
        'PGSRH offers men’s and women’s homes across the Lehigh Valley — explore our locations to compare.',
      ] },
    ],
    faqs: [
      { q: 'Is recovery housing different from sober living?', a: 'In most cases, no. They describe the same thing: a structured, substance-free home with peer support. At PGSRH the terms are interchangeable.' },
      { q: 'What about a “recovery residence”?', a: '“Recovery residence” is simply the more formal term for the same kind of home.' },
      { q: 'Do any of them provide treatment?', a: 'No. None of these are treatment. They are housing with support. PGSRH does not provide clinical services.' },
      { q: 'How do I choose the right one?', a: 'Compare gender-specific options, location, structure, and availability. Contact PGSRH and we’ll help you decide.' },
    ],
    funding: false,
  },
  {
    slug: '/how-long-stay-recovery-housing/',
    category: 'Educational Resource', tier: 'Tier 6',
    name: 'How Long Should You Stay in Recovery Housing?',
    primaryKeyword: 'How Long Should You Stay in Recovery Housing',
    keywords: ['how long in sober living', 'how long in recovery house', 'length of stay recovery housing'],
    title: 'How Long Should You Stay in Recovery Housing? | PGSRH',
    description: 'How long should you stay in recovery housing? There’s no single answer — it depends on your progress and needs. Learn what to consider with PGSRH in the Lehigh Valley, PA.',
    blurb: 'What to consider when thinking about length of stay.',
    heroSubtitle: 'There’s no one-size-fits-all answer — here’s how to think about length of stay in recovery housing.',
    intro: [
      'One of the most common questions residents and families ask is how long a stay in recovery housing should last. The honest answer is: it depends on the person, their progress, and their needs.',
      'This page walks through the factors that matter so you can have a realistic conversation about timing.',
    ],
    whoFor: [
      'Residents planning their recovery journey',
      'Families wondering how long a loved one may stay',
      'People weighing short-term vs. long-term options',
      'Referral partners setting expectations',
    ],
    sections: [
      { h: 'What affects length of stay', p: [
        'Stability in work and routine, strength of a support network, progress in recovery, and readiness for independent living all shape how long someone benefits from recovery housing. For many, more time means a more durable foundation.',
        'There is rarely a reason to rush. The goal is a confident, well-supported transition to independent living.',
      ] },
      { h: 'The PGSRH approach', p: [
        'PGSRH keeps length of stay flexible and based on individual needs rather than a rigid clock. If you want an extended stay, see our long-term recovery housing page.',
        'Talk with us about what makes sense for your situation — we’ll give you an honest perspective.',
      ] },
    ],
    faqs: [
      { q: 'Is there a minimum or maximum stay?', a: 'Length of stay is flexible and based on individual progress and needs. Contact PGSRH to discuss what fits your situation.' },
      { q: 'Is longer always better?', a: 'For many people, more time builds a stronger foundation, but the right length varies by person. The goal is a confident transition to independent living.' },
      { q: 'Can I extend my stay?', a: 'Extended stays are possible depending on availability and individual needs. See our long-term recovery housing page or ask us.' },
      { q: 'Does a longer stay cost more?', a: 'Cost is tied to length of stay. Funding or scholarship options may be available depending on eligibility and availability — ask PGSRH.' },
    ],
    funding: false,
  },
  {
    slug: '/benefits-of-recovery-housing/',
    category: 'Educational Resource', tier: 'Tier 6',
    name: 'Benefits of Recovery Housing',
    primaryKeyword: 'Benefits of Recovery Housing',
    keywords: ['benefits of sober living', 'recovery housing benefits', 'why sober living works'],
    title: 'The Benefits of Recovery Housing | PGSRH Lehigh Valley, PA',
    description: 'Why does recovery housing help? Structure, accountability, peer support, and stability all support lasting recovery. Learn the benefits with PGSRH in the Lehigh Valley, PA.',
    blurb: 'Why structure, peer support, and stability help recovery last.',
    heroSubtitle: 'Structure, accountability, peer support, stability — the everyday benefits that make recovery housing work.',
    intro: [
      'Recovery housing helps for reasons that are simple but powerful: it surrounds people with structure, accountability, peer support, and stability during a vulnerable stage of recovery.',
      'This page explains the practical benefits so you can see why so many people choose recovery housing after treatment.',
    ],
    whoFor: [
      'People weighing whether recovery housing is worth it',
      'Families wanting to understand the value',
      'Individuals looking for motivation to take the step',
      'Referral partners explaining benefits to clients',
    ],
    sections: [
      { h: 'The everyday benefits', p: [
        'Living with peers keeps people accountable and less isolated. A predictable routine rebuilds healthy habits. A substance-free home removes triggers. And clear expectations create a dependable environment where progress can take hold.',
        'These benefits compound over time — small daily wins add up to lasting change.',
      ] },
      { h: 'Beyond staying sober', p: [
        'Recovery housing also helps people rebuild the practical parts of life: steady employment, repaired relationships, and confidence for independent living.',
        'Explore our structured sober living and life-in-recovery pages to see these benefits in action.',
      ] },
    ],
    faqs: [
      { q: 'What are the main benefits of recovery housing?', a: 'Structure, accountability, peer support, a substance-free environment, and stability — all of which support lasting recovery.' },
      { q: 'Does recovery housing improve outcomes?', a: 'A supportive, structured environment can help people maintain recovery, though no home can guarantee outcomes. Recovery depends on many factors.' },
      { q: 'Is it only about staying sober?', a: 'No. It also helps people rebuild employment, relationships, and readiness for independent living.' },
      { q: 'How do I get started?', a: 'Explore our locations or contact PGSRH to ask about availability and next steps.' },
    ],
    funding: false,
  },
  {
    slug: '/first-week-recovery-house/',
    category: 'Educational Resource', tier: 'Tier 6',
    name: 'What to Expect During Your First Week in a Recovery House',
    primaryKeyword: 'First Week in a Recovery House',
    keywords: ['what to expect in sober living', 'first week sober living', 'first week recovery house'],
    title: 'Your First Week in a Recovery House: What to Expect | PGSRH',
    description: 'Nervous about your first week in a recovery house? Here’s what to expect — orientation, house expectations, routine, and support — with PGSRH in the Lehigh Valley, PA.',
    blurb: 'A realistic look at your first week in a recovery house.',
    heroSubtitle: 'A realistic, reassuring look at what your first week in a recovery house is like.',
    intro: [
      'The first week in a recovery house can feel intimidating — new place, new people, new routine. Knowing what to expect makes the transition far less stressful.',
      'This page walks through the basics so you can arrive prepared and confident.',
    ],
    whoFor: [
      'People about to move into a recovery house',
      'Families helping a loved one prepare',
      'Anyone nervous about the transition',
      'Referral partners setting client expectations',
    ],
    sections: [
      { h: 'Settling in', p: [
        'The first days usually involve getting oriented: understanding house expectations, meeting the people you’ll live with, finding your space, and learning the daily rhythm. Staff and peers help you get comfortable.',
        'It’s normal to feel unsure at first. Most residents find their footing quickly once the routine becomes familiar.',
      ] },
      { h: 'Building routine', p: [
        'Early on, you’ll start establishing a routine — meetings, responsibilities, and, where relevant, work or a job search. Structure is what makes the first week turn into steady progress.',
        'For more, see our pages on the rules of a recovery home and daily life in recovery housing.',
      ] },
    ],
    faqs: [
      { q: 'What happens on my first day?', a: 'You’ll get oriented to house expectations, meet other residents, settle into your space, and learn the daily routine.' },
      { q: 'Will I have rules to follow right away?', a: 'Yes. House expectations apply from day one and are explained clearly during orientation.' },
      { q: 'What should I bring?', a: 'Contact PGSRH before move-in and we’ll let you know what to bring and what to expect.' },
      { q: 'Is it normal to feel nervous?', a: 'Absolutely. Most residents feel more settled within the first week as the routine and community become familiar.' },
    ],
    funding: false,
  },
  {
    slug: '/rules-of-a-recovery-home/',
    category: 'Educational Resource', tier: 'Tier 6',
    name: 'Rules of a Recovery Home',
    primaryKeyword: 'Rules of a Recovery Home',
    keywords: ['sober living rules', 'recovery house rules', 'sober house rules'],
    title: 'The Rules of a Recovery Home: Why Structure Helps | PGSRH',
    description: 'What are the rules of a recovery home? Expectations like sobriety, accountability, and shared responsibilities create a safe, supportive environment. Learn more with PGSRH.',
    blurb: 'How house expectations create a safe, supportive home.',
    heroSubtitle: 'Why recovery homes have rules — and how those expectations create safety, structure, and support.',
    intro: [
      'Every good recovery home has clear expectations. They exist not to control people, but to keep the home safe, substance-free, and supportive for everyone living there.',
      'This page explains the kinds of expectations you can generally expect and why they matter.',
    ],
    whoFor: [
      'People curious about recovery home expectations',
      'Families wanting to understand the structure',
      'Individuals deciding if the structure fits them',
      'Anyone preparing to move into a recovery home',
    ],
    sections: [
      { h: 'Common expectations', p: [
        'Recovery homes typically expect residents to stay substance-free, respect other residents and shared spaces, contribute to household responsibilities, keep to house schedules, and stay engaged in their recovery. Specific expectations vary by home.',
        'These shared standards are what make a home dependable for everyone.',
      ] },
      { h: 'Why rules help', p: [
        'Clear expectations reduce chaos and create the predictability that early recovery needs. They also build accountability — the whole household holds a standard together.',
        'PGSRH explains its specific house expectations during the application and orientation process.',
      ] },
    ],
    faqs: [
      { q: 'What are typical recovery home rules?', a: 'Common expectations include staying substance-free, respecting others and shared spaces, contributing to chores, keeping to schedules, and staying engaged in recovery. Specifics vary by home.' },
      { q: 'Why are there so many rules?', a: 'Clear expectations keep the home safe and predictable, which is exactly what supports early recovery.' },
      { q: 'What are PGSRH’s specific rules?', a: 'We explain our specific house expectations during the application and orientation process. Contact us to learn more.' },
      { q: 'What if I struggle to follow them?', a: 'The community and structure exist to support you. Talk with staff and peers — accountability is meant to help, not punish.' },
    ],
    funding: false,
  },
  {
    slug: '/choosing-recovery-residence/',
    category: 'Educational Resource', tier: 'Tier 6',
    name: 'Choosing the Right Recovery Residence',
    primaryKeyword: 'Choosing the Right Recovery Residence',
    keywords: ['choose recovery housing', 'best recovery residence', 'how to choose sober living'],
    title: 'How to Choose the Right Recovery Residence | PGSRH',
    description: 'How do you choose the right recovery residence? Consider gender-specific options, location, structure, safety, and cost. A practical guide from PGSRH in the Lehigh Valley, PA.',
    blurb: 'A practical checklist for choosing a recovery residence.',
    heroSubtitle: 'A practical guide to choosing a recovery residence that truly fits your needs.',
    intro: [
      'Not every recovery residence is the same, and choosing the right one matters. This guide walks through the factors worth weighing so you can make a confident decision.',
      'Whether you’re choosing for yourself or a loved one, a few key questions will point you in the right direction.',
    ],
    whoFor: [
      'People comparing recovery residences',
      'Families choosing on behalf of a loved one',
      'Individuals unsure what to look for',
      'Referral partners guiding clients',
    ],
    sections: [
      { h: 'What to look for', p: [
        'Consider gender-specific vs. mixed housing, location relative to your support network, the home’s structure and expectations, safety, community, and cost. A good home is transparent about all of these.',
        'Ask questions freely — a reputable residence will welcome them.',
      ] },
      { h: 'How PGSRH measures up', p: [
        'PGSRH offers gender-specific homes across the Lehigh Valley, clear expectations, strong peer support, and honest conversations about availability and cost.',
        'Explore our locations and reach out with your questions — we’ll give you straight answers.',
      ] },
    ],
    faqs: [
      { q: 'What should I look for in a recovery residence?', a: 'Gender-specific options, location, structure and expectations, safety, community, and cost. Transparency about all of these is a good sign.' },
      { q: 'Are gender-specific homes better?', a: 'Many people do best in a home designed for their gender. PGSRH offers separate men’s and women’s homes.' },
      { q: 'What questions should I ask?', a: 'Ask about expectations, availability, cost, safety, and what daily life looks like. Reputable homes welcome these questions.' },
      { q: 'How do funding and scholarships factor in?', a: 'Cost matters. Funding or scholarship options may be available depending on eligibility and availability — ask PGSRH what may apply.' },
    ],
    funding: true,
  },
  {
    slug: '/recovery-housing-faq/',
    category: 'Educational Resource', tier: 'Tier 6',
    name: 'Recovery Housing FAQ',
    primaryKeyword: 'Recovery Housing FAQ',
    keywords: ['recovery housing questions', 'sober living FAQ', 'recovery house FAQ'],
    title: 'Recovery Housing FAQ | Common Questions Answered | PGSRH',
    description: 'Answers to common recovery housing questions — what it is, who it’s for, how to apply, house expectations, and funding options — from PGSRH in the Lehigh Valley, PA.',
    blurb: 'Answers to the most common recovery housing questions.',
    heroSubtitle: 'Straight answers to the questions people ask most about recovery housing.',
    intro: [
      'This page gathers the most common questions about recovery housing in one place — covering what it is, who it’s for, how to apply, house expectations, and funding.',
      'If you don’t see your question here, contact PGSRH and we’ll be glad to help.',
    ],
    whoFor: [
      'People new to recovery housing with lots of questions',
      'Families gathering information quickly',
      'Individuals preparing to apply',
      'Referral partners looking for clear answers',
    ],
    sections: [
      { h: 'The basics', p: [
        'Recovery housing is a safe, substance-free home with peer support and clear expectations. It is not treatment. PGSRH offers men’s and women’s homes in the Lehigh Valley and does not provide clinical services.',
        'Below you’ll find answers to the questions we hear most, including funding.',
      ] },
      { h: 'Still have questions?', p: [
        'Every situation is different. If your question isn’t answered here — about availability, expectations, or funding — reach out and we’ll give you an honest answer.',
        'You can call us, email us, or apply online.',
      ] },
    ],
    faqs: [
      { q: 'What is recovery housing?', a: 'A safe, substance-free home where people in recovery live together with shared expectations and peer support. It is housing, not clinical treatment.' },
      { q: 'Who can live there?', a: 'Adults in recovery committed to a substance-free lifestyle and willing to follow house expectations. PGSRH offers separate men’s and women’s homes.' },
      { q: 'How do I apply?', a: 'Apply online, call PGSRH, or have a case manager or treatment team reach out on your behalf.' },
      { q: 'Does recovery housing provide treatment?', a: 'No. PGSRH provides housing and peer support, not detox, rehab, therapy, or medical services.' },
      { q: 'Is there help paying for it?', a: 'Funding or scholarship options may be available depending on eligibility and availability. County, state, scholarship, and assistance programs each have their own requirements. Ask PGSRH; approval is not guaranteed.' },
      { q: 'How long can I stay?', a: 'Length of stay is flexible and based on individual progress and needs.' },
    ],
    funding: true,
  },
  {
    slug: '/life-in-recovery-housing/',
    category: 'Educational Resource', tier: 'Tier 6',
    name: 'Life in Recovery Housing',
    primaryKeyword: 'Life in Recovery Housing',
    keywords: ['living in recovery housing', 'life in sober living', 'sober living daily schedule'],
    title: 'Life in Recovery Housing: A Day in the Life | PGSRH',
    description: 'What is daily life in recovery housing really like? Routine, responsibilities, meetings, work, and community. A realistic look from PGSRH in the Lehigh Valley, PA.',
    blurb: 'A realistic look at daily life in recovery housing.',
    heroSubtitle: 'A realistic look at daily life in recovery housing — routine, responsibility, and community.',
    intro: [
      'What does daily life in recovery housing actually look like? For most residents, it looks a lot like healthy everyday life, with built-in structure and support.',
      'This page paints a realistic picture so you know what to expect — without overpromising outcomes.',
    ],
    whoFor: [
      'People picturing daily life in a recovery home',
      'Families imagining a loved one’s routine',
      'Individuals deciding if the lifestyle fits',
      'Anyone preparing to move in',
    ],
    sections: [
      { h: 'A typical day', p: [
        'Residents keep a routine: waking on time, going to work or looking for it, attending meetings, handling chores, and being present for the household. Evenings often include community time and rest.',
        'The rhythm is steady and predictable — that’s the point.',
      ] },
      { h: 'Community and accountability', p: [
        'Living with peers means built-in accountability and encouragement. Residents support one another through hard days and celebrate progress together.',
        'We’re careful not to promise outcomes — recovery depends on many factors — but a stable, supportive daily life gives people the best chance to keep moving forward.',
      ] },
    ],
    faqs: [
      { q: 'What is a typical day like?', a: 'Routine and responsibility: work or job searching, meetings, chores, and community time, all in a substance-free home.' },
      { q: 'Is there a fixed daily schedule?', a: 'Homes emphasize routine and expectations. Specifics are explained during orientation.' },
      { q: 'Will I have free time?', a: 'Yes. Life in recovery housing includes work, meetings, responsibilities, and personal and community time.' },
      { q: 'Does daily life include therapy?', a: 'No. PGSRH provides housing and peer support, not clinical services. Residents pursue any therapy with their own providers.' },
    ],
    funding: false,
  },
];

export default pages;
